import { type ComputedRef, type Ref, computed, ref } from 'vue'

import type {
  FunnelStep,
  GetQuestionsKind,
  Option,
  Question,
  Step,
  SummaryItem,
} from '@backmarket/http-api/src/api-specs-buyback/customer/getQuestionsV3'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { hashObjects } from '@backmarket/utils/object/hashObjects'
import { isEmpty } from '@backmarket/utils/object/isEmpty'

import translations from '../components/QuestionsForm/QuestionsForm.translations'

export type FunnelForm = Record<string, string | string[]>

interface Info {
  text: Array<string> | null
  tag: string
  subTag: string
}

export interface RenderOption extends Omit<Option, 'icon'> {
  inputId: string
  icon: string | undefined
  imagesSrc?: { src: string; srcMobile: string }
}

export interface RenderQuestion extends Omit<Question, 'options'> {
  value: string | false
  showInfo: boolean
  info: Info
  options: Array<RenderOption>
  isSelect: boolean
  isRadio: boolean
  isCheckbox: boolean
  isPickerWithImage: boolean
}

const QUESTION_TYPES = {
  SELECT: 'select',
  RADIO: 'radioline',
  CHECKBOX: 'checkbox',
} as const

const PICKER_WITH_IMAGE_KEYS = new Set(['state_screen', 'state_body'])

const DEFAULTS_LOOKUP: Record<string, string | false> = {
  [QUESTION_TYPES.RADIO]: false,
  [QUESTION_TYPES.SELECT]: '',
  [QUESTION_TYPES.CHECKBOX]: false,
}

interface UseFunnelQuestionsOptions {
  kind: GetQuestionsKind
  category: Ref<string>
  onStepChange?: (step: Step, formPayload: FunnelForm) => void
  onReadyForOffer: (formPayload: FunnelForm) => void
}

export type { Step, SummaryItem, FunnelStep }

export function useFunnelQuestions({
  kind,
  category,
  onStepChange,
  onReadyForOffer,
}: UseFunnelQuestionsOptions): {
  activeStep: ComputedRef<Step | null>
  activeQuestions: ComputedRef<RenderQuestion[]>
  funnel: Ref<FunnelStep[]>
  summary: Ref<SummaryItem[]>
  form: Ref<FunnelForm>
  isLoading: Ref<boolean>
  fetchError: Ref<Error | null>
  isStepComplete: ComputedRef<boolean>
  isLastQuestion: ComputedRef<boolean>
  hasSubmitButton: ComputedRef<boolean>
  buttonLabel: ComputedRef<string>
  fetchQuestionsWithPayload: (payload: FunnelForm) => Promise<void>
  handleAnswer: (key: string, value: string | false) => Promise<void>
  goToPreviousStep: () => Promise<void>
} {
  const i18n = useI18n()
  const $httpFetch = useHttpFetch()

  const funnel = ref<FunnelStep[]>([])
  const summary = ref<SummaryItem[]>([])
  const form = ref<FunnelForm>({})
  const isLoading = ref(false)
  const fetchError = ref<Error | null>(null)
  const history = ref<
    Record<string, { funnel: FunnelStep[]; summary: SummaryItem[] }>
  >({})

  const activeFunnel = computed(() =>
    funnel.value.find(({ step }) => step?.active),
  )

  const activeStep = computed<Step | null>(() => {
    return activeFunnel.value?.step ?? null
  })

  const isStepComplete = computed(() => activeStep.value?.completed || false)

  const activeStepQuestions = computed<Question[]>(() => {
    return activeFunnel.value?.questions || []
  })

  const isLastQuestion = computed(() =>
    activeStepQuestions.value.some((q) => q.lastQuestion === true),
  )

  const buttonLabel = computed(() =>
    isLastQuestion.value
      ? i18n(translations.seeOffer)
      : i18n(translations.continue),
  )

  // Implement a logic to display or not the submit button based on question type
  const hasSubmitButton = computed(() => false)

  const activeQuestions = computed<RenderQuestion[]>(() => {
    return activeStepQuestions.value.map(
      (question: Question): RenderQuestion => {
        const infoList = question.subTitleList
        const hasOptions = !isEmpty(question.options)
        const defaultValue = DEFAULTS_LOOKUP[question.type] ?? ''
        const preSelected = question.options.find((opt) => opt.selected)

        const rawValue = form.value[question.key]
        const formValue = typeof rawValue === 'string' ? rawValue : undefined
        const resolvedValue =
          formValue ?? (preSelected ? preSelected.value : defaultValue)
        const value: string | false =
          resolvedValue === false ? false : resolvedValue

        const isPickerWithImage =
          PICKER_WITH_IMAGE_KEYS.has(question.key) &&
          category.value === 'smartphone'

        return {
          ...question,
          value,
          showInfo: !isEmpty(infoList),
          info: {
            text: infoList,
            tag: infoList && infoList.length > 1 ? 'ul' : 'p',
            subTag: infoList && infoList.length > 1 ? 'li' : 'span',
          },
          options: hasOptions
            ? question.options.map((option) => ({
                ...option,
                inputId: `${option.label}-${option.value}`,
                icon: option?.icon?.light,
                imagesSrc: isPickerWithImage
                  ? {
                      src: `/img/buyback/${category.value}/${question.key}/${option.value}.png`,
                      srcMobile: `/img/buyback/${category.value}/${question.key}/${option.value}-mobile.png`,
                    }
                  : undefined,
              }))
            : [],
          infoBlock: question.infoBlock,
          isSelect: question.type === QUESTION_TYPES.SELECT,
          isRadio: question.type === QUESTION_TYPES.RADIO,
          isCheckbox: question.type === QUESTION_TYPES.CHECKBOX,
          isPickerWithImage,
        }
      },
    )
  })

  function populatePreSelectedValues(): void {
    const activeFunnelStep = funnel.value.find(({ step }) => step?.active)
    if (!activeFunnelStep) return

    const updates: FunnelForm = {}
    activeFunnelStep.questions.forEach((q) => {
      if (!(q.key in form.value)) {
        const preSelected = q.options.find((opt) => opt.selected)
        if (preSelected) {
          updates[q.key] = preSelected.value
        }
      }
    })

    if (!isEmpty(updates)) {
      form.value = { ...form.value, ...updates }
    }
  }

  async function fetchQuestionsWithPayload(payload: FunnelForm): Promise<void> {
    const { nextStep: _nextStep, ...formData } = payload
    form.value = { ...formData }

    const cacheKey = hashObjects(payload)
    const cached = history.value[cacheKey]

    if (cached) {
      funnel.value = cached.funnel
      summary.value = cached.summary
      populatePreSelectedValues()

      return
    }

    isLoading.value = true
    fetchError.value = null

    try {
      const response = await $httpFetch(
        '/buyback-funnel/api/v1/funnel/:kind/questions',
        {
          pathParams: { kind },
          query: payload,
        },
      )

      funnel.value = response?.funnel || []
      summary.value = response?.summary || []
      history.value[cacheKey] = { funnel: funnel.value, summary: summary.value }
      populatePreSelectedValues()
    } catch (error) {
      fetchError.value = error as Error
    } finally {
      isLoading.value = false
    }
  }

  async function handleAnswer(
    key: string,
    value: string | false,
  ): Promise<void> {
    if (value !== false) {
      form.value = { ...form.value, [key]: value }
    } else {
      const { [key]: _removed, ...rest } = form.value
      form.value = rest
    }

    if (activeStep.value) {
      onStepChange?.(activeStep.value, form.value)
    }

    await fetchQuestionsWithPayload({ ...form.value, nextStep: 'true' })

    if (isLastQuestion.value && isStepComplete.value) {
      onReadyForOffer(form.value)
    }
  }

  async function goToPreviousStep(): Promise<void> {
    const activeFunnelStep = funnel.value.find(({ step }) => step?.active)
    const backPayload: FunnelForm = { ...form.value }

    activeFunnelStep?.questions.forEach(({ key }) => {
      delete backPayload[key]
    })

    await fetchQuestionsWithPayload({ ...backPayload, nextStep: 'false' })
  }

  return {
    activeStep,
    activeQuestions,
    funnel,
    summary,
    form,
    isLoading,
    fetchError,
    isStepComplete,
    isLastQuestion,
    hasSubmitButton,
    buttonLabel,
    fetchQuestionsWithPayload,
    handleAnswer,
    goToPreviousStep,
  }
}
